/**
 * Wrap an async job so only one run is in progress at a time.
 *
 * A call while a run is in progress does not start a second, overlapping run. It asks for ONE
 * more run after the current one, and it returns a promise that settles when that run is done. So
 * a caller always waits for a run that STARTED after it asked, which is what a refresh needs: a
 * caller that asked because the token changed must not get the answer of a run that began with
 * the old token.
 *
 * ⚠ Many calls during one run give ONE more run, not one each.
 * ⚠ A failed run rejects every caller that waited for it, and the next call starts a new run.
 */
export function singleFlight(job: () => Promise<void>): () => Promise<void> {
  let running: Promise<void> | undefined
  let again = false

  const run = async () => {
    try {
      do {
        again = false
        await job()
      } while (again)
    } finally {
      again = false
      running = undefined
    }
  }

  return () => {
    if (running) {
      again = true
      return running
    }
    running = run()
    return running
  }
}
